import { createStyles, Grid, makeStyles, Theme, Typography } from '@material-ui/core';
import React from 'react';
import { MetaUrlGq } from '../../../../../graphql/projects/models/MetaUrlGq';
import { formatNumber } from '../../../../../logic/otherUtilities/formatting/formatNumber';
import { MetaRuleFormState } from './formLogic/MetaRuleFormState';

export interface MetaStatisticsProps {
  label: string;
  urlList: MetaUrlGq[];
  values: MetaRuleFormState;
  hint: boolean;
}

const useClasses = makeStyles((theme: Theme) =>
  createStyles({
    boldText: {
      fontWeight: 500,
    },
  })
);

export const MetaStatistics: React.FC<MetaStatisticsProps> = React.memo<MetaStatisticsProps>(
  ({ label, urlList, values, hint }) => {
    // Calculate stats
    const replacePlaceholders = (text: string, urlList: MetaUrlGq[]) => {
      return urlList?.map(
        (url) =>
          text
            ?.toLowerCase()
            .replace('[primary keyword]', url.primaryKeyword)
            .replace('[secondary keyword]', url.primaryKeyword).length
      );
    };

    const urlCount = urlList?.length > 0 ? urlList.length : 0;

    const titleCountList = replacePlaceholders(values.title, urlList);
    const metaCountList = replacePlaceholders(values.meta, urlList);

    const minTitleLength = 30;
    const maxTitleLength = 60;

    const minMetaLength = 100;
    const maxMetaLength = 160;

    // Titles
    const titlesTooLong = titleCountList?.filter((t) => t >= maxTitleLength).length;
    const titlesGood = titleCountList?.filter(
      (t) => t < maxTitleLength && t > minTitleLength
    ).length;
    const titlesTooShort = titleCountList?.filter((t) => t <= minTitleLength).length;

    const titlesTooLongFraction = 100 * (urlCount > 0 ? titlesTooLong / urlCount : urlCount);
    const titlesGoodFraction = 100 * (urlCount > 0 ? titlesGood / urlCount : urlCount);
    const titlesTooShortFraction = 100 * (urlCount > 0 ? titlesTooShort / urlCount : urlCount);

    // Meta
    const metaTooLong = metaCountList?.filter((t) => t >= maxMetaLength).length;
    const metaGood = metaCountList?.filter((t) => t < maxMetaLength && t > minMetaLength).length;
    const metaTooShort = metaCountList?.filter((t) => t <= minMetaLength).length;

    const metaTooLongFraction = 100 * (urlCount > 0 ? metaTooLong / urlCount : urlCount);
    const metaGoodFraction = 100 * (urlCount > 0 ? metaGood / urlCount : urlCount);
    const metaTooShortFraction = 100 * (urlCount > 0 ? metaTooShort / urlCount : urlCount);

    // Styling
    const classes = useClasses();

    return (
      <div className='w-1/2 pr-8'>
        <Typography
          variant={'subtitle1'}
          className={classes.boldText}
          color={hint ? 'textSecondary' : 'textPrimary'}
        >
          {label}
        </Typography>
        <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'} paragraph>
          Total urls: {urlCount}
        </Typography>

        <Grid container spacing={4}>
          <Grid item container xs={6}>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                Titles
              </Typography>
            </Grid>
            <Grid container item>
              <Grid item xs>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  Too long:
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  {titlesTooLong} ({formatNumber(titlesTooLongFraction)}%)
                </Typography>
              </Grid>
            </Grid>
            <Grid container item>
              <Grid item xs>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  Good:
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  {titlesGood} ({formatNumber(titlesGoodFraction)}%)
                </Typography>
              </Grid>
            </Grid>
            <Grid container item>
              <Grid item xs>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  Too short:
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  {titlesTooShort} ({formatNumber(titlesTooShortFraction)}%)
                </Typography>
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={6}>
            <Grid item xs={12}>
              <Typography variant={'subtitle2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                Meta descriptions
              </Typography>
            </Grid>
            <Grid container item>
              <Grid item xs>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  Too long:
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  {metaTooLong} ({formatNumber(metaTooLongFraction)}%)
                </Typography>
              </Grid>
            </Grid>

            <Grid container item>
              <Grid item xs>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  Good:
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  {metaGood} ({formatNumber(metaGoodFraction)}%)
                </Typography>
              </Grid>
            </Grid>

            <Grid container item>
              <Grid item xs>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  Too short:
                </Typography>
              </Grid>
              <Grid item>
                <Typography variant={'body2'} color={hint ? 'textSecondary' : 'textPrimary'}>
                  {metaTooShort} ({formatNumber(metaTooShortFraction)}%)
                </Typography>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </div>
    );
  }
);

export default MetaStatistics;
